import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaTools,
  FaClipboardList,
  FaBoxes,
  FaHistory,
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
} from 'react-icons/fa';
import axios from 'axios';

const Vaerksted = () => {
  const [activeTab, setActiveTab] = useState('oversigt');
  const navigate = useNavigate();

  // Eftersyn state
  const [eftersynList, setEftersynList] = useState([]);
  const [eftersynLoading, setEftersynLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEftersyn, setEditingEftersyn] = useState(null);
  const [mekanikere, setMekanikere] = useState([]);
  const [selectedSignature, setSelectedSignature] = useState('');
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [chainRows, setChainRows] = useState([{ description: '', weight: '' }]);
  const addChainRow = () => setChainRows(prev => [...prev, { description: '', weight: '' }]);
  const removeChainRow = (idx) => setChainRows(prev => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  const [ladderRows, setLadderRows] = useState([{ ladder_number: '', ladder_type: '', ladder_material: '', manufacturer: '', ladder_part_number: '', acquisition_date: '' }]);
  const [ladderTypeOptions, setLadderTypeOptions] = useState([]);
  const [ladderMaterialOptions, setLadderMaterialOptions] = useState([]);
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [stigeFieldDefs, setStigeFieldDefs] = useState([]);
  
  // Initialiser ladderRows når stigeFieldDefs ændres
  useEffect(() => {
    if (stigeFieldDefs.length > 0 && ladderRows.length === 1 && Object.keys(ladderRows[0]).length === 0) {
      const initialRow = {};
      stigeFieldDefs.forEach(field => {
        initialRow[field.name] = '';
      });
      setLadderRows([initialRow]);
    }
  }, [stigeFieldDefs, ladderRows]);
  const addLadderRow = () => {
    // Initialiser alle felter baseret på stigeFieldDefs eller hardkodede felter som fallback
    const newRow = {};
    if (stigeFieldDefs.length > 0) {
      stigeFieldDefs.forEach(field => {
        newRow[field.name] = '';
      });
    } else {
      // Fallback til hardkodede felter
      newRow.ladder_number = '';
      newRow.ladder_type = '';
      newRow.ladder_material = '';
      newRow.manufacturer = '';
      newRow.ladder_part_number = '';
      newRow.acquisition_date = '';
    }
    setLadderRows(prev => [...prev, newRow]);
  };
  const removeLadderRow = (idx) => setLadderRows(prev => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  const AFDELINGER = ['Personvogns afdeling', 'Lastvogns afdeling', 'Plade afdeling', 'Smede afdeling'];
  const DEPT_TYPES = ['El eftersyn', 'Kæde eftersyn', 'Stige eftersyn'];
  const OTHER_TYPES = [
    'Kemiskab',
    'Frigørelsesværktøj',
    'Nilfisk gulvvasker',
    'Lift eftersyn',
    'Port eftersyn',
    'Faldsikring',
    'Førstehjælp/øjenskyld',
  ];
  const [selectedAfdeling, setSelectedAfdeling] = useState('');
  const [highlightFlow, setHighlightFlow] = useState(false);
  const formatDateInput = (dateObj) => {
    const pad = (n) => String(n).padStart(2, '0');
    const y = dateObj.getFullYear();
    const m = pad(dateObj.getMonth() + 1);
    const d = pad(dateObj.getDate());
    return `${y}-${m}-${d}`;
  };
  const getDefaultFormData = () => {
    const today = new Date();
    const next = new Date(today);
    next.setFullYear(today.getFullYear() + 1);
    return {
      inventar_navn: '',
      afdeling: '',
      eftersyn_dato: formatDateInput(today),
      eftersyn_type: '',
      beskrivelse: '',
      status: 'Planlagt',
      noter: '',
      godkendt_status: 'Godkendt',
      ansvarlig: '',
      godkendt_af: '',
      sidste_eftersyn: '',
      naeste_eftersyn: formatDateInput(next),
    };
  };
  const [formData, setFormData] = useState(getDefaultFormData());
  const isDeptType = (t) => DEPT_TYPES.includes(t);

  const startCreateEftersyn = (type) => {
    console.log('startCreateEftersyn kaldt med type:', type);
    const defaults = getDefaultFormData();
    const dept = isDeptType(type);
    // Kræv afdeling for afdelingsbundne typer
    const afdelingValue = dept ? selectedAfdeling : '';
    setEditingEftersyn(null);
    setFormData({
      ...defaults,
      eftersyn_type: type,
      afdeling: afdelingValue,
    });
    // reset modal-specific state
    setApproved('Godkendt');
    setDescriptions(['']);
    setSelectedSignature('');
    setLadderRows([{ ladder_number: '', ladder_type: '', ladder_material: '', manufacturer: '', ladder_part_number: '', acquisition_date: '' }]); // Reset ladder rows
    setShowModal(true);

    // Hent Stige-eftersyn options fra services-skema, hvis relevant
    if ((type || '').toLowerCase().includes('stige')) {
      console.log('Kalder fetchStigeOptions for type:', type);
      fetchStigeOptions();
    } else {
      // Reset ladderRows for ikke-stige eftersyn
      setLadderRows([{ ladder_number: '', ladder_type: '', ladder_material: '', manufacturer: '', ladder_part_number: '', acquisition_date: '' }]);
    }
  };

  const fetchStigeOptions = async () => {
    try {
      console.log('Starter fetchStigeOptions...');
      // Find kategori-id for Stigeeftersyn (eksakt navn)
      const cats = await axios.get('/api/services/categories');
      const all = cats.data || [];
      console.log('Alle kategorier:', all);
      const stigeCats = all.filter((c) => {
        const n = (c.name || '').toLowerCase().trim();
        console.log('Tjekker kategori:', n);
        return n.includes('årseftersyn') || n.includes('annual') || n.includes('inspection');
      });
      console.log('Stige kategorier fundet:', stigeCats);
      if (!stigeCats.length) {
        console.warn('Ingen stige-kategorier fundet, prøver subcategory_id 28 direkte');
        // Prøv direkte med subcategory_id 28
        const fieldPromises = [28].map(id => axios.get(`/api/indstillinger/eftersynsskema/subcategories/${id}/fields`).then(r => ({ id, fields: r.data || [] })));
        const fieldsArr = await Promise.all(fieldPromises);
        console.log('Fields array (direkte):', fieldsArr);
        const allFields = fieldsArr.flatMap(x => x.fields || []).sort((a,b)=> (a.sort_order||0)-(b.sort_order||0));
        console.log('StigeFieldDefs hentet (direkte):', allFields);
        setStigeFieldDefs(allFields);
        
        const collectOpts = (name) => {
          const opts = [];
          const seen = new Set();
          allFields.forEach(ff => {
            if ((ff.name || '').toLowerCase() !== name) return;
            try {
              const arr = ff.options ? JSON.parse(ff.options) : [];
              (arr || []).forEach(v => {
                if (!seen.has(v)) { seen.add(v); opts.push(v); }
              });
            } catch (_) {}
          });
          return opts;
        };
        setLadderTypeOptions(collectOpts('ladder_type'));
        setLadderMaterialOptions(collectOpts('ladder_material'));
        setManufacturerOptions(collectOpts('manufacturer'));
        
        // Initialiser ladderRows med de korrekte felter
        const initialRow = {};
        if (allFields.length > 0) {
          allFields.forEach(field => {
            initialRow[field.name] = '';
          });
        } else {
          // Fallback til hardkodede felter hvis der ikke findes nogen inspection_fields
          initialRow.ladder_number = '';
          initialRow.ladder_type = '';
          initialRow.ladder_material = '';
          initialRow.manufacturer = '';
          initialRow.ladder_part_number = '';
          initialRow.acquisition_date = '';
        }
        console.log('Initialiserer ladderRows med (direkte):', initialRow);
        setLadderRows([initialRow]);
        return;
      }
      const ids = stigeCats.map(c => c.id);
      console.log('Kategori IDs:', ids);
      const resp = await axios.get(`/api/services/categories/${ids.join(',')}`);
      const withSubs = resp.data || [];
      console.log('Med underkategorier:', withSubs);
      // Brug underkategorier der indeholder "stige" eller "ladder"
      const subIds = Array.from(new Set(
        withSubs
          .filter(x => x.subcategory_id && ((x.subcategory_name || '').toLowerCase().includes('stige') || (x.subcategory_name || '').toLowerCase().includes('ladder')))
          .map(x => x.subcategory_id)
      ));
      
      // Hvis vi ikke finder nogen underkategorier, prøv at finde subcategory_id 28 direkte
      if (subIds.length === 0) {
        console.log('Ingen stige underkategorier fundet, prøver subcategory_id 28 direkte');
        subIds.push(28);
      }
      console.log('Subcategory IDs fundet:', subIds);
      if (!subIds.length) {
        console.warn('Ingen stige-underkategorier fundet');
        return;
      }
      const fieldPromises = subIds.map(id => {
        console.log('Henter felter for subcategory_id:', id);
        return axios.get(`/api/indstillinger/eftersynsskema/subcategories/${id}/fields`).then(r => {
          console.log(`Felter for subcategory_id ${id}:`, r.data);
          return { id, fields: r.data || [] };
        }).catch(err => {
          console.error(`Fejl ved hentning af felter for subcategory_id ${id}:`, err);
          return { id, fields: [] };
        });
      });
      const fieldsArr = await Promise.all(fieldPromises);
      console.log('Fields array:', fieldsArr);
      // Sorter felter efter sort_order for korrekt rækkefølge
      const allFields = fieldsArr.flatMap(x => x.fields || []).sort((a,b)=> (a.sort_order||0)-(b.sort_order||0));
      console.log('StigeFieldDefs hentet:', allFields);
      
      if (allFields.length === 0) {
        console.warn('Ingen felter fundet for stige underkategorier');
        // Prøv direkte med subcategory_id 28
        try {
          const directResponse = await axios.get(`/api/indstillinger/eftersynsskema/subcategories/28/fields`);
          console.log('Direkte response for subcategory_id 28:', directResponse.data);
          const directFields = (directResponse.data || []).sort((a,b)=> (a.sort_order||0)-(b.sort_order||0));
          console.log('Direkte felter sorteret:', directFields);
          setStigeFieldDefs(directFields);
        } catch (err) {
          console.error('Fejl ved direkte hentning af subcategory_id 28:', err);
          setStigeFieldDefs([]);
        }
      } else {
        setStigeFieldDefs(allFields);
      }
      const collectOpts = (name) => {
        const opts = [];
        const seen = new Set();
        const fieldsToUse = stigeFieldDefs.length > 0 ? stigeFieldDefs : allFields;
        fieldsToUse.forEach(ff => {
          if ((ff.name || '').toLowerCase() !== name) return;
          try {
            const arr = ff.options ? JSON.parse(ff.options) : [];
            (arr || []).forEach(v => {
              if (!seen.has(v)) { seen.add(v); opts.push(v); }
            });
          } catch (_) {}
        });
        return opts;
      };
      setLadderTypeOptions(collectOpts('ladder_type'));
      setLadderMaterialOptions(collectOpts('ladder_material'));
      setManufacturerOptions(collectOpts('manufacturer'));
      
      // Initialiser ladderRows med de korrekte felter
      const initialRow = {};
      const fieldsToUse = stigeFieldDefs.length > 0 ? stigeFieldDefs : allFields;
      if (fieldsToUse.length > 0) {
        fieldsToUse.forEach(field => {
          initialRow[field.name] = '';
        });
      } else {
        // Fallback til hardkodede felter hvis der ikke findes nogen inspection_fields
        initialRow.ladder_number = '';
        initialRow.ladder_type = '';
        initialRow.ladder_material = '';
        initialRow.manufacturer = '';
        initialRow.ladder_part_number = '';
        initialRow.acquisition_date = '';
      }
      console.log('Initialiserer ladderRows med:', initialRow);
      setLadderRows([initialRow]);
    } catch (e) {
      // Faldbak: behold evt. eksisterende options
      console.error('Fejl ved hentning af stige-options:', e);
      // Prøv direkte med subcategory_id 28 som sidste udvej
      try {
        const directResponse = await axios.get(`/api/indstillinger/eftersynsskema/subcategories/28/fields`);
        console.log('Direkte response for subcategory_id 28 (fallback):', directResponse.data);
        const directFields = (directResponse.data || []).sort((a,b)=> (a.sort_order||0)-(b.sort_order||0));
        console.log('Direkte felter sorteret (fallback):', directFields);
        setStigeFieldDefs(directFields);
        
        const collectOpts = (name) => {
          const opts = [];
          const seen = new Set();
          directFields.forEach(ff => {
            if ((ff.name || '').toLowerCase() !== name) return;
            try {
              const arr = ff.options ? JSON.parse(ff.options) : [];
              (arr || []).forEach(v => {
                if (!seen.has(v)) { seen.add(v); opts.push(v); }
              });
            } catch (_) {}
          });
          return opts;
        };
        setLadderTypeOptions(collectOpts('ladder_type'));
        setLadderMaterialOptions(collectOpts('ladder_material'));
        setManufacturerOptions(collectOpts('manufacturer'));
        
        // Initialiser ladderRows med de korrekte felter
        const initialRow = {};
        if (directFields.length > 0) {
          directFields.forEach(field => {
            initialRow[field.name] = '';
          });
        } else {
          // Fallback til hardkodede felter hvis der ikke findes nogen inspection_fields
          initialRow.ladder_number = '';
          initialRow.ladder_type = '';
          initialRow.ladder_material = '';
          initialRow.manufacturer = '';
          initialRow.ladder_part_number = '';
          initialRow.acquisition_date = '';
        }
        console.log('Initialiserer ladderRows med (fallback):', initialRow);
        setLadderRows([initialRow]);
      } catch (fallbackErr) {
        console.error('Fejl ved fallback hentning af subcategory_id 28:', fallbackErr);
        setStigeFieldDefs([]);
      }
    }
  };

  const handleCreateCtaClick = () => {
    try {
      const el = document.getElementById('eftersynFlow');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setHighlightFlow(true);
        setTimeout(() => setHighlightFlow(false), 2000);
      }
    } catch (_) {}
  };

  // Simpelt indholdsskema: godkendt/ikke godkendt + beskrivelse (flere rækker)
  const [approved, setApproved] = useState('Godkendt');
  const [descriptions, setDescriptions] = useState(['']);
  const addDescRow = () => setDescriptions(prev => [...prev, '']);
  const removeDescRow = (idx) => setDescriptions(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  // Statistik
  const [stats, setStats] = useState({
    planlagteEftersyn: 0,
    iGang: 0,
    afsluttedeSeneste30: 0,
    inventarAntal: 0,
    afdelingsStats: [],
  });

  useEffect(() => {
    // Hent eftersyn ved load så statistik-kort vises korrekt
    fetchEftersyn();
    // Hent mekanikere til underskrift dropdown
    axios.get('/api/services/mekanikere')
      .then(r => setMekanikere(r.data || []))
      .catch(() => setMekanikere([]));
    // Hent afdelings-statistik
    axios.get('/api/vaerksted/eftersyn/stats/afdelinger')
      .then(r => setStats(prev => ({ ...prev, afdelingsStats: Array.isArray(r.data) ? r.data : [] })))
      .catch(() => setStats(prev => ({ ...prev, afdelingsStats: [] })));
  }, []);

  const recomputeStats = (items) => {
    try {
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const planlagte = items.filter((e) => (e.status || '').toLowerCase() === 'planlagt').length;
      const iGang = items.filter((e) => (e.status || '').toLowerCase() === 'i gang').length;
      const afsluttede30 = items.filter((e) => {
        const isDone = (e.status || '').toLowerCase() === 'afsluttet';
        if (!isDone) return false;
        const d = e.eftersyn_dato ? new Date(e.eftersyn_dato) : null;
        return d && d >= thirtyDaysAgo;
      }).length;
      setStats((prev) => ({ ...prev, planlagteEftersyn: planlagte, iGang, afsluttedeSeneste30: afsluttede30 }));
    } catch (_) {
      // Ignorer evt. parsing-fejl
    }
  };

  const fetchEftersyn = async () => {
    setEftersynLoading(true);
    try {
      const response = await axios.get('/api/vaerksted/eftersyn');
      setEftersynList(response.data || []);
      recomputeStats(response.data || []);
    } catch (error) {
      console.error('Error fetching eftersyn:', error);
      setEftersynList([]);
      recomputeStats([]);
    } finally {
      setEftersynLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEftersyn) {
        await axios.put(`/api/vaerksted/eftersyn/${editingEftersyn.id}`, formData);
      } else {
        const res = await axios.post('/api/vaerksted/eftersyn', formData);
        const newId = res?.data?.id;
        if (newId) {
          const optimistic = {
            id: newId,
            afdeling: formData.afdeling || '',
            eftersyn_dato: formData.eftersyn_dato || null,
            eftersyn_type: formData.eftersyn_type || '',
            status: formData.status || 'Planlagt',
            beskrivelse: formData.beskrivelse || ''
          };
          setEftersynList(prev => [optimistic, ...prev]);
        }
      }
      setShowModal(false);
      setEditingEftersyn(null);
      setFormData(getDefaultFormData());
      setSearchTerm('');
      fetchEftersyn();
    } catch (error) {
      console.error('Error saving eftersyn:', error);
    }
  };

  const handleEdit = (eft) => {
    setEditingEftersyn(eft);
    setFormData({
      inventar_navn: eft.inventar_navn || '',
      afdeling: eft.afdeling || '',
      eftersyn_dato: eft.eftersyn_dato ? String(eft.eftersyn_dato).slice(0, 10) : '',
      eftersyn_type: eft.eftersyn_type || '',
      beskrivelse: eft.beskrivelse || '',
      status: eft.status || 'Planlagt',
      noter: eft.noter || '',
      godkendt_status: eft.godkendt_status || 'Godkendt',
      ansvarlig: eft.ansvarlig || '',
      godkendt_af: eft.godkendt_af || '',
      sidste_eftersyn: eft.sidste_eftersyn ? String(eft.sidste_eftersyn).slice(0, 10) : '',
      naeste_eftersyn: eft.naeste_eftersyn ? String(eft.naeste_eftersyn).slice(0, 10) : getDefaultFormData().naeste_eftersyn,
    });
    setLadderRows([{ ladder_number: '', ladder_type: '', ladder_material: '', manufacturer: '', ladder_part_number: '', acquisition_date: '' }]); // Reset ladder rows
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Er du sikker på, at du vil slette dette eftersyn?')) {
      try {
        await axios.delete(`/api/vaerksted/eftersyn/${id}`);
        fetchEftersyn();
      } catch (error) {
        console.error('Error deleting eftersyn:', error);
      }
    }
  };

  const handleRenew = async (eft) => {
    try {
      const today = new Date();
      const next = new Date(today);
      next.setFullYear(today.getFullYear() + 1);
      const pad = (n) => String(n).padStart(2, '0');
      const toDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      await axios.put(`/api/vaerksted/eftersyn/${eft.id}`, {
        eftersyn_dato: toDate(today),
        naeste_eftersyn: toDate(next),
        status: 'Planlagt'
      });
      fetchEftersyn();
    } catch (error) {
      console.error('Error renewing eftersyn:', error);
      alert('Kunne ikke forny eftersynet');
    }
  };

  const handleReuse = (eft) => {
    try {
      if (isDeptType(eft.eftersyn_type) && eft.afdeling) {
        setSelectedAfdeling(eft.afdeling);
      }
      setLadderRows([{ ladder_number: '', ladder_type: '', ladder_material: '', manufacturer: '', ladder_part_number: '', acquisition_date: '' }]); // Reset ladder rows
      startCreateEftersyn(eft.eftersyn_type);
    } catch (_) {}
  };

  const filteredEftersyn = eftersynList.filter((eft) =>
    (eft.afdeling || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (eft.eftersyn_type || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (eft.status || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (eft.beskrivelse || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Værksted</h1>
          <p className="text-gray-600">Oversigt over afdelinger, eftersyn og inventar</p>
        </div>
      </div>

      {/* Statistik Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <FaClipboardList className="text-falck-blue text-2xl mr-3" />
            <div>
              <p className="text-sm text-gray-600">Planlagte eftersyn</p>
              <p className="text-2xl font-bold text-gray-900">{stats.planlagteEftersyn}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <FaTools className="text-yellow-600 text-2xl mr-3" />
            <div>
              <p className="text-sm text-gray-600">I gang</p>
              <p className="text-2xl font-bold text-gray-900">{stats.iGang}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <FaHistory className="text-green-600 text-2xl mr-3" />
            <div>
              <p className="text-sm text-gray-600">Afsluttede (30 dage)</p>
              <p className="text-2xl font-bold text-gray-900">{stats.afsluttedeSeneste30}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <FaBoxes className="text-blue-600 text-2xl mr-3" />
            <div>
              <p className="text-sm text-gray-600">Registreret inventar</p>
              <p className="text-2xl font-bold text-gray-900">{stats.inventarAntal}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <div className="overflow-x-auto no-scrollbar">
            <nav className="-mb-px flex space-x-6 sm:space-x-8 whitespace-nowrap px-1">
              <button
                onClick={() => setActiveTab('oversigt')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'oversigt'
                    ? 'border-falck-blue text-falck-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FaTools className="inline mr-2" />
                Oversigt
              </button>
              <button
                onClick={() => setActiveTab('eftersyn')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'eftersyn'
                    ? 'border-falck-blue text-falck-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FaClipboardList className="inline mr-2" />
                Eftersyn
              </button>
              <button
                onClick={() => setActiveTab('inventar')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'inventar'
                    ? 'border-falck-blue text-falck-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FaBoxes className="inline mr-2" />
                Inventar
              </button>
              <button
                onClick={() => setActiveTab('historik')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'historik'
                    ? 'border-falck-blue text-falck-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FaHistory className="inline mr-2" />
                Historik
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow">
        {activeTab === 'oversigt' && (
          <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
            <h2 className="text-xl font-semibold mb-4">Afdelinger</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {stats.afdelingsStats.length === 0 ? (
                <div className="p-4 rounded-lg border border-gray-200 text-gray-600">Ingen data</div>
              ) : (
                stats.afdelingsStats.map((row, idx) => (
                  <div key={idx} className="p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600">{row.afdeling}</p>
                      <span className="text-xs text-gray-500">Total: {row.total}</span>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                      <div className="bg-white border border-gray-100 rounded p-2">
                        <div className="text-gray-600">El</div>
                        <div className="text-lg font-semibold text-gray-900">{row.el || 0}</div>
                      </div>
                      <div className="bg-white border border-gray-100 rounded p-2">
                        <div className="text-gray-600">Kæde</div>
                        <div className="text-lg font-semibold text-gray-900">{row.kaede || 0}</div>
                      </div>
                      <div className="bg-white border border-gray-100 rounded p-2">
                        <div className="text-gray-600">Stige</div>
                        <div className="text-lg font-semibold text-gray-900">{row.stige || 0}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'eftersyn' && (
          <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl font-semibold">Eftersyn</h2>
              <button
                type="button"
                onClick={handleCreateCtaClick}
                className="bg-falck-blue text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
              >
                <FaPlus /> Opret eftersyn
              </button>
            </div>

            {/* Opret eftersyn - flow */}
            <div id="eftersynFlow" className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-3 sm:p-4 mb-4 ${highlightFlow ? 'ring-2 ring-blue-400' : ''}`}>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Afdelinger</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {AFDELINGER.map((afd) => (
                  <button
                    key={afd}
                    type="button"
                    onClick={() => setSelectedAfdeling((cur) => (cur === afd ? '' : afd))}
                    className={`px-3 py-1.5 rounded-full border text-sm ${
                      selectedAfdeling === afd
                        ? 'bg-falck-blue text-white border-falck-blue'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300'
                    }`}
                  >
                    {afd}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
                {DEPT_TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => startCreateEftersyn(t)}
                    disabled={!selectedAfdeling}
                    className={`px-3 py-3 rounded-lg border text-left ${
                      selectedAfdeling
                        ? 'hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer'
                        : 'opacity-60 cursor-not-allowed'
                    }`}
                    title={selectedAfdeling ? '' : 'Vælg først en afdeling'}
                  >
                    <div className="font-medium text-gray-800 dark:text-gray-100">{t}</div>
                    <div className="text-xs text-gray-500">Afdelingsbundet</div>
                  </button>
                ))}
              </div>

              <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Øvrige eftersyn (ikke afdelingsbundne)</p>
              <div className="flex flex-wrap gap-2">
                {OTHER_TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => startCreateEftersyn(t)}
                    className="px-3 py-1.5 rounded-full border text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Søgefelt */}
            <div className="mb-4">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Søg efter eftersyn..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                />
              </div>
            </div>

            {/* Tabel */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow overflow-hidden">
              {eftersynLoading ? (
                <div className="flex justify-center items-center h-40">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-falck-blue"></div>
                </div>
              ) : (
                <div className="scroll-fade-x"><div className="scroll-area">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Afdeling</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Eftersyn dato</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Beskrivelse</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Handlinger</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredEftersyn.length === 0 ? (
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" colSpan={6}>Ingen eftersyn fundet</td>
                        </tr>
                      ) : (
                        filteredEftersyn.map((eft) => (
                          <tr key={eft.id} className="hover:bg-gray-50 cursor-pointer" onClick={async () => {
                            try {
                              const resp = await axios.get(`/api/vaerksted/eftersyn/${eft.id}/report`);
                              const html = resp.data?.html || '';
                              if (html) {
                                const win = window.open('', '_blank');
                                if (win) {
                                  win.document.write(html);
                                  win.document.close();
                                  win.focus();
                                }
                              }
                            } catch (e) {
                              alert('Kunne ikke hente rapport');
                            }
                          }}>
                             <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{eft.afdeling && eft.afdeling.trim() ? eft.afdeling : '—'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{eft.eftersyn_dato ? new Date(eft.eftersyn_dato).toLocaleDateString('da-DK') : '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{eft.eftersyn_type || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  eft.status === 'Planlagt'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : eft.status === 'I gang'
                                    ? 'bg-blue-100 text-blue-800'
                                    : eft.status === 'Afsluttet'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {eft.status || '-'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">{eft.beskrivelse || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end gap-2">
                                <button onClick={() => handleRenew(eft)} className="text-falck-blue hover:text-blue-700 px-2 py-1 border border-falck-blue rounded">Forny</button>
                                <button onClick={() => handleReuse(eft)} className="text-gray-700 hover:text-gray-900 px-2 py-1 border border-gray-400 rounded">Genbrug</button>
                                <button onClick={() => handleDelete(eft.id)} className="text-red-600 hover:text-red-900 px-2 py-1 border border-red-600 rounded">Slet</button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div></div>
              )}
            </div>

            {/* Modal */}
            {showModal && (
              <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-2 sm:p-0 bg-black/50">
                <div className="bg-white w-full max-w-[95vw] sm:max-w-md md:max-w-lg rounded-t-2xl sm:rounded-lg p-4 sm:p-6 shadow-xl max-h-[85vh] overflow-y-auto">
                  <h2 className="text-lg sm:text-xl font-bold mb-4">{editingEftersyn ? 'Rediger eftersyn' : 'Tilføj nyt eftersyn'}</h2>
                  <form onSubmit={(e) => {
                    // simpelt payload for indhold
                    try {
                      if (formData.eftersyn_type === 'Kæde eftersyn') {
                        const descriptionsArr = chainRows.map(r => (r.description || '').trim());
                        const weightArr = chainRows.map(r => (r.weight || '').trim());
                        formData.content_json = JSON.stringify({ description: descriptionsArr, weight: weightArr, signature: selectedSignature });
                      } else if (formData.eftersyn_type === 'Stige eftersyn') {
                        // Byg payload baseret på stigeFieldDefs
                        const payload = { signature: selectedSignature };
                        stigeFieldDefs.forEach(field => {
                          payload[field.name] = ladderRows.map(r => r[field.name] || '');
                        });
                        formData.content_json = JSON.stringify(payload);
                      } else {
                        formData.content_json = JSON.stringify({ descriptions, signature: selectedSignature });
                      }
                    } catch (_) {}
                    handleSubmit(e);
                  }} className="space-y-4">
                    {/* Fjernet Eftersyn type og Inventar pr. ønske */}
                    {isDeptType(formData.eftersyn_type) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Afdeling</label>
                        <select
                          value={formData.afdeling}
                          onChange={(e) => setFormData({ ...formData, afdeling: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                          required
                        >
                          <option value="">Vælg afdeling</option>
                          {AFDELINGER.map((afd) => (
                            <option key={afd} value={afd}>{afd}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {/* Datoer er forudfyldt i state (i dag og +1 år) */}

                    {/* Indholdsskema per type */}
                    {formData.eftersyn_type === 'Kæde eftersyn' ? (
                      <div className="border border-gray-200 rounded-lg p-3 sm:p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Kædeeftersyn</label>
                        {chainRows.map((row, idx) => (
                          <div key={idx} className="grid grid-cols-1 sm:grid-cols-5 gap-2 mb-2">
                            <div className="sm:col-span-3">
                              <input
                                type="text"
                                value={row.description}
                                onChange={(e) => setChainRows(prev => prev.map((r,i) => i===idx ? { ...r, description: e.target.value } : r))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                                placeholder="Beskrivelse"
                              />
                            </div>
                            <div className="sm:col-span-1">
                              <input
                                type="text"
                                value={row.weight}
                                onChange={(e) => setChainRows(prev => prev.map((r,i) => i===idx ? { ...r, weight: e.target.value } : r))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                                placeholder="Vægt/Kg"
                              />
                            </div>
                            <div className="sm:col-span-1 flex items-center justify-end">
                              <button type="button" onClick={() => removeChainRow(idx)} className="text-red-600 hover:text-red-800 text-sm">Fjern række</button>
                            </div>
                          </div>
                        ))}
                        <button type="button" onClick={addChainRow} className="text-falck-blue hover:text-blue-700 text-sm font-medium">+ Tilføj en linje mere</button>
                        <div className="mt-3 relative">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Underskrift (mekaniker/ansvarlig)</label>
                          <input
                            type="text"
                            value={selectedSignature}
                            onChange={(e) => setSelectedSignature(e.target.value)}
                            onFocus={() => setSignatureOpen(true)}
                            onBlur={() => setTimeout(() => setSignatureOpen(false), 150)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                            placeholder="Søg eller skriv frit navn"
                          />
                          {signatureOpen && selectedSignature && (
                            <div className="absolute z-10 mt-1 w-full max-h-48 overflow-auto bg-white border border-gray-200 rounded-md shadow">
                              {mekanikere
                                .filter((m) => (m || '').toLowerCase().includes(selectedSignature.toLowerCase()))
                                .slice(0, 10)
                                .map((m, i) => (
                                  <div
                                    key={i}
                                    className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      setSelectedSignature(m);
                                      setSignatureOpen(false);
                                    }}
                                  >
                                    {m}
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : formData.eftersyn_type === 'Stige eftersyn' ? (
                      <div className="border border-gray-200 rounded-lg p-3 sm:p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Stigeeftersyn</label>
                        {console.log('Rendering Stigeeftersyn - stigeFieldDefs:', stigeFieldDefs, 'ladderRows:', ladderRows)}
                        {ladderRows.map((row, idx) => (
                          <div key={idx} className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                            {/* Render felter baseret på inspection_fields fra subcategory_id 28 */}
                            {stigeFieldDefs.length > 0 ? stigeFieldDefs.map(field => {
                              const value = row[field.name] || '';
                              const onChange = (val) => setLadderRows(prev => prev.map((r, i) => i === idx ? { ...r, [field.name]: val } : r));
                              
                              if (field.type === 'select') {
                                let options = [];
                                try { options = field.options ? JSON.parse(field.options) : []; } catch (_) { options = []; }
                                return (
                                  <div key={field.name}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                                    <select
                                      value={value}
                                      onChange={(e) => onChange(e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                                    >
                                      <option value="">-- Vælg --</option>
                                      {options.map((opt, i) => (
                                        <option key={i} value={opt}>{opt}</option>
                                      ))}
                                    </select>
                                  </div>
                                );
                              }
                              
                              if (field.type === 'date') {
                                return (
                                  <div key={field.name}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                                    <input
                                      type="date"
                                      value={value}
                                      onChange={(e) => onChange(e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                                    />
                                  </div>
                                );
                              }
                              
                              // Default text input
                              return (
                                <div key={field.name}>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                                  <input
                                    type="text"
                                    value={value}
                                    onChange={(e) => onChange(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                                    placeholder={field.label}
                                  />
                                </div>
                              );
                            }) : (
                              <div className="sm:col-span-2 text-gray-500 text-sm">
                                Ingen felter fundet. Henter data...
                                {/* Fallback til hardkodede felter hvis der ikke findes nogen inspection_fields */}
                                <div className="mt-2 space-y-2">
                                  <input type="text" value={row.ladder_number || ''} onChange={(e)=>setLadderRows(prev=>prev.map((r,i)=>i===idx?{...r, ladder_number:e.target.value}:r))} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Stigens nummer" />
                                  <select value={row.ladder_type || ''} onChange={(e)=>setLadderRows(prev=>prev.map((r,i)=>i===idx?{...r, ladder_type:e.target.value}:r))} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                                    <option value="">-- Vælg stige type --</option>
                                    {ladderTypeOptions.map((opt)=> (
                                      <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                  </select>
                                  <select value={row.ladder_material || ''} onChange={(e)=>setLadderRows(prev=>prev.map((r,i)=>i===idx?{...r, ladder_material:e.target.value}:r))} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                                    <option value="">-- Vælg materiale --</option>
                                    {ladderMaterialOptions.map((opt)=> (
                                      <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                  </select>
                                  <select value={row.manufacturer || ''} onChange={(e)=>setLadderRows(prev=>prev.map((r,i)=>i===idx?{...r, manufacturer:e.target.value}:r))} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                                    <option value="">-- Vælg producent --</option>
                                    {manufacturerOptions.map((opt)=> (
                                      <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                  </select>
                                  <input type="text" value={row.ladder_part_number || ''} onChange={(e)=>setLadderRows(prev=>prev.map((r,i)=>i===idx?{...r, ladder_part_number:e.target.value}:r))} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Stige varenummer" />
                                  <input type="date" value={row.acquisition_date || ''} onChange={(e)=>setLadderRows(prev=>prev.map((r,i)=>i===idx?{...r, acquisition_date:e.target.value}:r))} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Dato for anskaffelsen" />
                                </div>
                              </div>
                            )}
                            {ladderRows.length > 1 && (
                              <div className="sm:col-span-2 flex justify-end">
                                <button type="button" onClick={()=>removeLadderRow(idx)} className="text-red-600 hover:text-red-800 text-sm">Fjern række</button>
                              </div>
                            )}
                          </div>
                        ))}
                        <button type="button" onClick={addLadderRow} className="text-falck-blue hover:text-blue-700 text-sm font-medium">+ Tilføj en linje mere</button>
                        <div className="mt-3 relative">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Underskrift (mekaniker/ansvarlig)</label>
                          <input
                            type="text"
                            value={selectedSignature}
                            onChange={(e)=>setSelectedSignature(e.target.value)}
                            onFocus={() => setSignatureOpen(true)}
                            onBlur={() => setTimeout(() => setSignatureOpen(false), 150)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="Søg eller skriv frit navn"
                          />
                          {signatureOpen && selectedSignature && (
                            <div className="absolute z-10 mt-1 w-full max-h-48 overflow-auto bg-white border border-gray-200 rounded-md shadow">
                              {mekanikere
                                .filter((m) => (m || '').toLowerCase().includes(selectedSignature.toLowerCase()))
                                .slice(0, 10)
                                .map((m, i) => (
                                  <div
                                    key={i}
                                    className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      setSelectedSignature(m);
                                      setSignatureOpen(false);
                                    }}
                                  >
                                    {m}
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="border border-gray-200 rounded-lg p-3 sm:p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Beskrivelse (hvad er tjekket)</label>
                            {descriptions.map((d, idx) => (
                              <div key={idx} className="flex items-start gap-2 mb-2">
                                <textarea
                                  value={d}
                                  onChange={(e) => setDescriptions(prev => prev.map((v, i) => i === idx ? e.target.value : v))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                                  rows={3}
                                  placeholder="Fx: Isolationstest, besigtigelse af kabler, funktionstest af afbryder ..."
                                />
                                <button type="button" onClick={() => removeDescRow(idx)} className="text-red-600 hover:text-red-800 text-sm whitespace-nowrap px-2 py-1 mt-1">Fjern række</button>
                              </div>
                            ))}
                            <button type="button" onClick={addDescRow} className="text-falck-blue hover:text-blue-700 text-sm font-medium">+ Tilføj en linje mere</button>
                          </div>
                          <div className="sm:col-span-2 relative">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Underskrift (mekaniker/ansvarlig)</label>
                            <input
                              type="text"
                              value={selectedSignature}
                              onChange={(e) => setSelectedSignature(e.target.value)}
                              onFocus={() => setSignatureOpen(true)}
                              onBlur={() => setTimeout(() => setSignatureOpen(false), 150)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                              placeholder="Søg eller skriv frit navn"
                            />
                            {signatureOpen && selectedSignature && (
                              <div className="absolute z-10 mt-1 w-full max-h-48 overflow-auto bg-white border border-gray-200 rounded-md shadow">
                                {mekanikere
                                  .filter((m) => (m || '').toLowerCase().includes(selectedSignature.toLowerCase()))
                                  .slice(0, 10)
                                  .map((m, i) => (
                                    <div
                                      key={i}
                                      className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        setSelectedSignature(m);
                                        setSignatureOpen(false);
                                      }}
                                    >
                                      {m}
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {/* øvrige felter udeladt – hold modal enkel */}
                    <div className="flex flex-col sm:flex-row justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowModal(false);
                          setEditingEftersyn(null);
                          setFormData(getDefaultFormData());
                          setApproved('Godkendt');
                          setDescriptions(['']);
                          setSelectedSignature('');
                          setLadderRows([{ ladder_number: '', ladder_type: '', ladder_material: '', manufacturer: '', ladder_part_number: '', acquisition_date: '' }]); // Reset ladder rows
                        }}
                        className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300 w-full sm:w-auto"
                      >
                        Annuller
                      </button>
                      <button type="submit" className="px-4 py-2 bg-falck-blue text-white rounded-md hover:bg-blue-700 w-full sm:w-auto">
                        {editingEftersyn ? 'Opdater' : 'Tilføj'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'inventar' && (
          <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
            <h2 className="text-xl font-semibold mb-4">Inventar</h2>
            <div className="scroll-fade-x"><div className="scroll-area">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Navn</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Afdeling</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sidst tjekket</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" colSpan={4}>Ingen inventar registreret</td>
                  </tr>
                </tbody>
              </table>
            </div></div>
          </div>
        )}

        {activeTab === 'historik' && (
          <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
            <h2 className="text-xl font-semibold mb-4">Historik</h2>
            <div className="scroll-fade-x"><div className="scroll-area">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dato</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Handling</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bruger</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detaljer</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" colSpan={4}>Ingen historik endnu</td>
                  </tr>
                </tbody>
              </table>
            </div></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Vaerksted;
